import React, { useState, useEffect, useMemo } from 'react';
import { startOfWeek, addDays, format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { patientBookingAPI, clinicianAPI, patientBookingRequestAPI, ClinicianService } from '../../services/api';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { InformationCircleIcon, ExclamationTriangleIcon, ChevronDoubleRightIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { formatClinicDate, formatTimeSlot, clinicTimeToUTC } from '../../utils/timezone';
import { apiErrorMessage } from '../../utils/apiError';

/**
 * BookingCalendar Props
 *
 * Phase 4: Unified clinician interface supporting both:
 * - Legacy assigned clinician flow (email-based)
 * - Multi-clinician flow (slug-based, from directory or invite codes)
 */
interface BookingCalendarProps {
  clinician: {
    id: number;
    name: string;
    email?: string;        // Legacy flow (assigned clinician)
    specialty?: string;
    slug?: string;         // Phase 4: URL slug for multi-clinician flow
    requires_booking_approval?: boolean;  // Phase 4: Public + Approval mode
    is_public?: boolean;   // Phase 4: Clinician visibility mode
    booking_mode?: 'time' | 'service';  // Phase 3d: service-based booking
    services?: ClinicianService[];       // Phase 3d: service menu
  };
}

interface TimeSlot {
  date: string;
  hour: number;
  minute: number;        // 0 or 30 for 30-min slots
  is_available: boolean;
  is_booked: boolean;
  has_room: boolean;     // Room availability
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({ clinician }) => {
  // Phase 4: Derive booking mode from clinician props
  // - isApprovalMode: Public + Approval clinicians require request approval
  // - clinicianSlug: Used for URL-based availability fetching (multi-clinician flow)
  const isApprovalMode = clinician.requires_booking_approval ?? false;
  const clinicianSlug = clinician.slug;

  // Phase 3d: service-based booking — patient picks a service (duration derived)
  // instead of choosing a duration. Falls back to time mode if no services.
  const services = clinician.services ?? [];
  const isServiceMode = (clinician.booking_mode === 'service') && services.length > 0;
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const selectedService = services.find((s) => s.id === selectedServiceId) || null;

  // In service mode, the chosen service drives duration + attendees.
  useEffect(() => {
    if (isServiceMode && selectedService) {
      setSelectedDuration(selectedService.duration_minutes);
      setNumAttendees(selectedService.capacity ?? 1);
      setSelectedStartSlot(null);
      setSelectedSlots([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceId, isServiceMode]);

  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [selectedSlots, setSelectedSlots] = useState<TimeSlot[]>([]);
  const [selectedDuration, setSelectedDuration] = useState<number>(60); // Default 1 hour
  const [selectedStartSlot, setSelectedStartSlot] = useState<TimeSlot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [notes, setNotes] = useState('');
  const [numAttendees, setNumAttendees] = useState<number>(1);
  const [bookingSuccess, setBookingSuccess] = useState<{
    isRequest: boolean;
    date: string;
    time: string;
    duration: string;
  } | null>(null);
  const navigate = useNavigate();

  // Week pagination state
  const [weekOffset, setWeekOffset] = useState(0);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  // Fetch 28 days of availability
  useEffect(() => {
    const loadAvailability = async () => {
      // If no clinician info available, show selection prompt
      if (!clinician) {
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setAccessDenied(false);

        let availabilityData;

        // Use clinician-specific API when we have a slug (Phase 4 multi-clinician flow)
        if (clinicianSlug) {
          availabilityData = await clinicianAPI.getClinicianAvailability(clinicianSlug, 28, numAttendees);
        } else {
          // Legacy flow: fetch from assigned clinician
          availabilityData = await patientBookingAPI.getAvailability(28, numAttendees);
        }

        setSlots(availabilityData);
        // Clear selection when availability changes (e.g., numAttendees changed)
        setSelectedSlots([]);
      } catch (error: any) {
        console.error('Failed to load availability:', error);
        let errorMessage = 'Failed to load availability';

        if (error.response?.status === 401) {
          errorMessage = 'Please log in to view availability';
        } else if (error.response?.status === 403) {
          // Access denied - likely private clinician without connection
          setAccessDenied(true);
          errorMessage = error.response?.data?.detail ||
            "You don't have access to this clinician's calendar.";
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message?.includes('CORS') || error.message?.includes('ERR_FAILED')) {
          errorMessage = 'Connection error. Please check if the backend server is running.';
        }

        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadAvailability();

    // Auto-refresh availability every 30 seconds to keep data fresh
    const refreshInterval = setInterval(async () => {
      if (!clinician) return;

      try {
        let availabilityData;
        if (clinicianSlug) {
          availabilityData = await clinicianAPI.getClinicianAvailability(clinicianSlug, 28, numAttendees);
        } else {
          availabilityData = await patientBookingAPI.getAvailability(28, numAttendees);
        }
        setSlots(availabilityData);
      } catch (error) {
        console.error('Failed to refresh availability:', error);
      }
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [numAttendees, clinicianSlug, clinician]);

  // Reset week offset when clinician or numAttendees changes
  useEffect(() => {
    setWeekOffset(0);
  }, [clinician?.id, numAttendees]);

  // Calculate week boundaries
  const getWeekRange = (offset: number) => {
    const today = new Date();
    const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 }); // Monday
    const weekStart = addDays(startOfCurrentWeek, offset * 7);
    const weekEnd = addDays(weekStart, 6);
    return { weekStart, weekEnd };
  };

  const { weekStart, weekEnd } = getWeekRange(weekOffset);

  // Filter slots to current week and group by day
  const { slotsByDay, sortedDays } = useMemo(() => {
    // Filter to current week
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

    const currentWeekSlots = slots.filter(slot => {
      return slot.date >= weekStartStr && slot.date <= weekEndStr;
    });

    // Group by day
    const grouped: Record<string, TimeSlot[]> = {};
    currentWeekSlots.forEach(slot => {
      if (!grouped[slot.date]) {
        grouped[slot.date] = [];
      }
      grouped[slot.date].push(slot);
    });

    // Sort slots within each day
    Object.values(grouped).forEach(daySlots => {
      daySlots.sort((a, b) => {
        if (a.hour !== b.hour) return a.hour - b.hour;
        return a.minute - b.minute;
      });
    });

    // Get sorted day keys
    const sorted = Object.keys(grouped).sort();

    return { slotsByDay: grouped, sortedDays: sorted };
  }, [slots, weekStart, weekEnd]);

  // Increment in minutes (matches backend BOOKING_INCREMENT_MINUTES)
  const INCREMENT = 15;

  // Number of consecutive slots needed for the selected duration
  const slotsNeeded = selectedDuration / INCREMENT;

  // Find available start times for the selected duration within a day's slots
  const getAvailableStartTimes = (daySlots: TimeSlot[]): TimeSlot[] => {
    const bookable = daySlots.filter(s => s.is_available && s.has_room && !s.is_booked);
    const startTimes: TimeSlot[] = [];

    for (let i = 0; i <= bookable.length - slotsNeeded; i++) {
      const window = bookable.slice(i, i + slotsNeeded);
      // Check all slots in window are consecutive
      let isConsecutive = true;
      for (let j = 1; j < window.length; j++) {
        const prevMin = window[j - 1].hour * 60 + window[j - 1].minute;
        const currMin = window[j].hour * 60 + window[j].minute;
        if (currMin - prevMin !== INCREMENT || window[j].date !== window[0].date) {
          isConsecutive = false;
          break;
        }
      }
      if (isConsecutive) {
        startTimes.push(window[0]);
      }
    }
    return startTimes;
  };

  // Count bookable slots for a day (available + has_room + !is_booked)
  const getBookableCount = (daySlots: TimeSlot[]): number => {
    return getAvailableStartTimes(daySlots).length;
  };

  // Check if a day has any bookable slots
  const hasBookableSlots = (daySlots: TimeSlot[]): boolean => {
    return getBookableCount(daySlots) > 0;
  };

  // Find first day with bookable slots (memoized for dependency stability)
  const firstBookableDay = useMemo(() => {
    return sortedDays.find(day => {
      const daySlots = slotsByDay[day];
      return daySlots && getBookableCount(daySlots) > 0;
    }) || null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedDays, slotsByDay, selectedDuration]);

  // Find next week with available slots (for "Jump to Next Available" button)
  const findNextAvailableWeek = useMemo(() => {
    // Check weeks after current one (up to week 3)
    for (let offset = weekOffset + 1; offset <= 3; offset++) {
      const { weekStart: ws, weekEnd: we } = getWeekRange(offset);
      const weekStartStr = format(ws, 'yyyy-MM-dd');
      const weekEndStr = format(we, 'yyyy-MM-dd');

      // Check if any start times exist for the selected duration in this week
      const weekSlots = slots.filter(slot =>
        slot.date >= weekStartStr && slot.date <= weekEndStr
      );
      const byDay: Record<string, TimeSlot[]> = {};
      weekSlots.forEach(s => { if (!byDay[s.date]) byDay[s.date] = []; byDay[s.date].push(s); });
      const hasAvailability = Object.values(byDay).some(ds => getAvailableStartTimes(ds).length > 0);

      if (hasAvailability) {
        return offset;
      }
    }
    return null; // No available weeks found
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, weekOffset, selectedDuration]);

  // Jump to next available week
  const jumpToNextAvailable = () => {
    if (findNextAvailableWeek !== null) {
      setWeekOffset(findNextAvailableWeek);
    }
  };

  // Auto-expand first day with bookable slots when week changes
  useEffect(() => {
    if (firstBookableDay) {
      setExpandedDays(new Set([firstBookableDay]));
    } else {
      setExpandedDays(new Set());
    }
  }, [weekOffset, firstBookableDay]);

  // Toggle day expansion
  const toggleDay = (dateKey: string) => {
    // Don't allow expanding days with no bookable slots
    if (!hasBookableSlots(slotsByDay[dateKey])) return;

    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };

  const handleStartTimeClick = (startSlot: TimeSlot) => {
    // Find all bookable slots for this day
    const daySlots = slotsByDay[startSlot.date] || [];
    const bookable = daySlots.filter(s => s.is_available && s.has_room && !s.is_booked);

    // Find the consecutive window starting from this slot
    const startIdx = bookable.findIndex(
      s => s.date === startSlot.date && s.hour === startSlot.hour && s.minute === startSlot.minute
    );

    if (startIdx === -1) return;

    const window = bookable.slice(startIdx, startIdx + slotsNeeded);

    // Verify consecutive
    if (window.length < slotsNeeded) return;
    for (let j = 1; j < window.length; j++) {
      const prevMin = window[j - 1].hour * 60 + window[j - 1].minute;
      const currMin = window[j].hour * 60 + window[j].minute;
      if (currMin - prevMin !== INCREMENT) return;
    }

    // If clicking the same start time, deselect
    if (selectedStartSlot &&
        selectedStartSlot.date === startSlot.date &&
        selectedStartSlot.hour === startSlot.hour &&
        selectedStartSlot.minute === startSlot.minute) {
      setSelectedStartSlot(null);
      setSelectedSlots([]);
    } else {
      setSelectedStartSlot(startSlot);
      setSelectedSlots(window);
    }
  };

  const handleBooking = async () => {
    if (isServiceMode && !selectedServiceId) {
      toast.error('Please choose a service first');
      return;
    }
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }

    if (!clinician) {
      toast.error('No clinician selected');
      return;
    }

    try {
      setIsBooking(true);

      // Use the first selected slot as start time
      const startSlot = selectedSlots[0];

      // TIMEZONE HANDLING: Convert clinic local time to UTC for API
      // Availability slots are in clinic local time (America/Edmonton)
      // We convert to UTC so the backend stores the correct absolute time
      const startTimeISO = clinicTimeToUTC(startSlot.date, startSlot.hour, startSlot.minute);

      const durationMinutes = selectedDuration;

      // Capture booking details for success display before clearing state
      const successDate = formatDateDisplay(startSlot.date);
      const endSlot = selectedSlots[selectedSlots.length - 1];
      const endTime = getEndTime(endSlot);
      const successTime = `${formatSlotTime(startSlot.hour, startSlot.minute)} - ${formatSlotTime(endTime.hour, endTime.minute)}`;
      const successDuration = formatDuration(durationMinutes);

      // Service mode: send service_id; backend derives duration + attendees.
      const serviceId = isServiceMode && selectedServiceId ? selectedServiceId : undefined;

      if (isApprovalMode) {
        // Public + Approval mode: Create booking request instead of direct booking
        await patientBookingRequestAPI.createRequest({
          clinician_id: clinician.id,
          start_time: startTimeISO,
          duration_minutes: durationMinutes,
          num_attendees: numAttendees,
          service_id: serviceId,
          notes: notes.trim() || undefined,
        });
      } else {
        // Direct booking mode (Private after connect, or Public + Direct)
        const bookingData = {
          clinician_id: clinician.id,
          start_time: startTimeISO,
          duration_minutes: durationMinutes,
          num_attendees: numAttendees,
          service_id: serviceId,
          notes: notes.trim() || undefined,
        };

        await patientBookingAPI.createBooking(bookingData);
      }

      // Show prominent success screen
      setBookingSuccess({
        isRequest: isApprovalMode,
        date: successDate,
        time: successTime,
        duration: successDuration,
      });

      setSelectedSlots([]);
      setNotes('');

      // Refresh availability
      let availabilityData;
      if (clinicianSlug) {
        availabilityData = await clinicianAPI.getClinicianAvailability(clinicianSlug, 28, numAttendees);
      } else {
        availabilityData = await patientBookingAPI.getAvailability(28, numAttendees);
      }
      setSlots(availabilityData);

    } catch (error: any) {
      let errorMessage = isApprovalMode ? 'Failed to submit request' : 'Failed to create booking';

      if (error.response?.status === 400) {
        // Booking conflict or validation error
        errorMessage = apiErrorMessage(error, 'Selected time slot is no longer available');
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      toast.error(errorMessage);

      // Refresh availability to show current state
      try {
        let refreshedAvailability;
        if (clinicianSlug) {
          refreshedAvailability = await clinicianAPI.getClinicianAvailability(clinicianSlug, 28, numAttendees);
        } else {
          refreshedAvailability = await patientBookingAPI.getAvailability(28, numAttendees);
        }
        setSlots(refreshedAvailability);
        setSelectedSlots([]); // Clear selection since slots may have changed
      } catch (refreshError) {
        console.error('Failed to refresh availability:', refreshError);
      }
    } finally {
      setIsBooking(false);
    }
  };

  // Format time with hour and minute for 30-min slot display
  // Uses centralized timezone utility for clinic-local display
  const formatSlotTime = (hour: number, minute: number): string => {
    return formatTimeSlot(hour, minute);
  };

  // Format duration in minutes to human-readable string
  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours} hr`;
    return `${hours} hr ${mins} min`;
  };

  // Format date for display - uses clinic timezone
  // Date-only strings (YYYY-MM-DD) represent local dates at the clinic
  const formatDateDisplay = (dateString: string) => {
    return formatClinicDate(dateString, 'EEEE, MMM d');
  };

  // Format date for selection display - uses clinic timezone
  const formatDateShort = (dateString: string) => {
    return formatClinicDate(dateString, 'EEE, MMM d');
  };

  // Booking success screen
  if (bookingSuccess) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <CheckCircleIcon className="mx-auto h-16 w-16 text-clinic-primary" />
        <h2 className="mt-4 text-2xl font-bold text-clinic-dark">
          {bookingSuccess.isRequest ? 'Request Submitted' : 'Booking Confirmed'}
        </h2>
        <p className="mt-2 text-clinic-muted">
          {bookingSuccess.isRequest
            ? `Your appointment request with ${clinician.name} has been submitted. You'll receive a response within 48 hours.`
            : `Your appointment with ${clinician.name} has been confirmed.`
          }
        </p>

        <div className="mt-6 bg-clinic-cream/50 rounded-lg p-4 inline-block text-left">
          <div className="space-y-2 text-sm">
            <div><span className="font-medium text-clinic-dark">Clinician:</span> <span className="text-clinic-muted">{clinician.name}</span></div>
            <div><span className="font-medium text-clinic-dark">Date:</span> <span className="text-clinic-muted">{bookingSuccess.date}</span></div>
            <div><span className="font-medium text-clinic-dark">Time:</span> <span className="text-clinic-muted">{bookingSuccess.time}</span></div>
            <div><span className="font-medium text-clinic-dark">Duration:</span> <span className="text-clinic-muted">{bookingSuccess.duration}</span></div>
          </div>
        </div>

        {!bookingSuccess.isRequest && (
          <p className="mt-4 text-sm text-clinic-muted">
            A confirmation email has been sent to your email address.
          </p>
        )}

        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <button
            onClick={() => navigate('/patient/appointments')}
            className="w-full sm:w-auto px-6 py-2.5 min-h-[44px] bg-clinic-primary text-white rounded-lg hover:bg-clinic-primary-dark transition-colors"
          >
            View My Bookings
          </button>
          <button
            onClick={() => setBookingSuccess(null)}
            className="w-full sm:w-auto px-6 py-2.5 min-h-[44px] border border-clinic-primary text-clinic-primary rounded-lg hover:bg-clinic-primary/5 transition-colors"
          >
            Book Another
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  // Access denied - private clinician without connection
  if (accessDenied) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-amber-500" />
          <h3 className="mt-4 text-lg font-medium text-clinic-dark">Unable to View Availability</h3>
          <p className="mt-2 text-clinic-muted max-w-md mx-auto">
            {error || "You don't have access to this clinician's calendar."}
          </p>
          <p className="mt-2 text-sm text-clinic-muted">
            This may be because the clinician requires an invite code to connect.
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <Link
              to="/patient/my-clinicians"
              className="px-4 py-2 bg-clinic-primary text-white rounded-md hover:bg-clinic-primary"
            >
              Enter Invite Code
            </Link>
            <Link
              to="/clinicians"
              className="px-4 py-2 border border-clinic-primary-light text-clinic-dark rounded-md hover:bg-clinic-surface"
            >
              Browse Public Clinicians
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // General error state
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <ErrorMessage message={error} />
        <div className="mt-4">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-clinic-primary text-white rounded-md hover:bg-clinic-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Calculate end time from a start slot + selected duration
  const getEndTime = (slot: TimeSlot): { hour: number; minute: number } => {
    const totalMinutes = slot.hour * 60 + slot.minute + selectedDuration;
    return { hour: Math.floor(totalMinutes / 60), minute: totalMinutes % 60 };
  };

  // Format week range for header
  const weekRangeText = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;

  // Button text based on mode
  const bookButtonText = isApprovalMode
    ? `Request Appointment (${formatDuration(selectedDuration)})`
    : `Book ${formatDuration(selectedDuration)}`;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-clinic-dark">
          {isApprovalMode ? 'Request Appointment' : 'Book Appointment'}
        </h2>
        <p className="text-clinic-muted">with {clinician.name}</p>
      </div>

      {isServiceMode ? (
        /* Step 1 (service mode): Choose a service — duration + attendees derived */
        <div className="mb-6">
          <label className="block text-sm font-medium text-clinic-dark mb-2">Choose a Service</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {services.map((s) => {
              const priceNum = s.price === null || s.price === undefined || s.price === '' ? null : Number(s.price);
              const active = selectedServiceId === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSelectedServiceId(s.id)}
                  className={`text-left rounded-lg border p-4 transition-colors ${
                    active
                      ? 'border-clinic-primary bg-clinic-primary/5 ring-1 ring-clinic-primary'
                      : 'border-clinic-primary-lighter hover:bg-clinic-cream/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-clinic-dark">{s.name}</span>
                    {priceNum !== null && !Number.isNaN(priceNum) && (
                      <span className="text-sm font-medium text-clinic-dark flex-shrink-0">${priceNum.toFixed(0)}</span>
                    )}
                  </div>
                  <div className="text-sm text-clinic-muted mt-0.5">
                    {formatDuration(s.duration_minutes)}{s.capacity > 1 ? ` · up to ${s.capacity} people` : ''}
                  </div>
                  {s.description && <p className="text-sm text-clinic-muted mt-1">{s.description}</p>}
                </button>
              );
            })}
          </div>
          {services.some((s) => s.price !== null && s.price !== undefined && s.price !== '') && (
            <p className="text-xs text-clinic-muted mt-2">
              Payment is arranged with your clinician — you won't be charged online.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Step 1 (time mode): Duration Selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-clinic-dark mb-2">Session Length</label>
            <div className="flex flex-wrap gap-2">
              {[30, 60, 90, 120].map(dur => (
                <button
                  key={dur}
                  onClick={() => { setSelectedDuration(dur); setSelectedStartSlot(null); setSelectedSlots([]); }}
                  className={`px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium transition-colors border ${
                    selectedDuration === dur
                      ? 'bg-clinic-primary text-white border-clinic-primary'
                      : 'bg-white text-clinic-dark border-clinic-primary-lighter hover:bg-clinic-cream/50'
                  }`}
                >
                  {formatDuration(dur)}
                </button>
              ))}
            </div>
          </div>

          {/* Number of Attendees */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-clinic-dark mb-1">Number of Attendees</label>
            <input
              type="number"
              min={1}
              value={numAttendees}
              onChange={(e) => setNumAttendees(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-24 px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
            />
            <p className="text-xs text-clinic-muted mt-1">How many people will be attending?</p>
          </div>
        </>
      )}

      {/* Selected time summary */}
      {selectedStartSlot && (
        <div className="mb-4 bg-clinic-primary/10 border border-clinic-primary/20 rounded-lg p-4">
          <p className="text-sm font-medium text-clinic-dark">
            {formatDateShort(selectedStartSlot.date)} &middot;{' '}
            {formatSlotTime(selectedStartSlot.hour, selectedStartSlot.minute)} -{' '}
            {(() => { const end = getEndTime(selectedStartSlot); return formatSlotTime(end.hour, end.minute); })()}{' '}
            ({formatDuration(selectedDuration)})
          </p>
        </div>
      )}

      {/* Approval Mode Notice */}
      {isApprovalMode && (
        <div className="bg-clinic-primary-lighter/40 border border-clinic-primary-light rounded-md p-4 mb-4">
          <div className="flex items-start">
            <InformationCircleIcon className="h-5 w-5 text-clinic-primary mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-clinic-primary">
                <span className="font-medium">This clinician reviews requests before confirming.</span>
              </p>
              <p className="text-sm text-clinic-primary mt-1">
                After submitting your request, you'll receive a response within 48 hours.
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Service mode: prompt to choose a service before showing times */}
      {isServiceMode && !selectedServiceId && (
        <div className="text-center py-8 text-clinic-muted border border-dashed border-clinic-primary-lighter rounded-lg">
          <p className="font-medium text-clinic-dark">Choose a service above to see available times.</p>
        </div>
      )}

      {/* Calendar (hidden until a service is chosen in service mode) */}
      {(!isServiceMode || selectedServiceId) && (
      <>
      {/* Week Navigation Header */}
      <div className="flex items-center justify-between gap-2 bg-clinic-cream/50 rounded-lg p-3 mb-4">
        <button
          onClick={() => setWeekOffset(prev => Math.max(0, prev - 1))}
          disabled={weekOffset === 0}
          className={`flex items-center gap-1.5 px-3 py-1.5 min-h-[44px] rounded-md text-sm font-medium transition-colors flex-shrink-0 ${
            weekOffset === 0
              ? 'text-clinic-muted cursor-not-allowed'
              : 'text-clinic-primary hover:bg-clinic-primary/10'
          }`}
        >
          <ChevronLeftIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Prev Week</span>
          <span className="sm:hidden">Prev</span>
        </button>

        <span className="font-medium text-clinic-dark text-sm sm:text-base text-center min-w-0">
          {weekRangeText}
        </span>

        <button
          onClick={() => setWeekOffset(prev => Math.min(3, prev + 1))}
          disabled={weekOffset >= 3}
          className={`flex items-center gap-1.5 px-3 py-1.5 min-h-[44px] rounded-md text-sm font-medium transition-colors flex-shrink-0 ${
            weekOffset >= 3
              ? 'text-clinic-muted cursor-not-allowed'
              : 'text-clinic-primary hover:bg-clinic-primary/10'
          }`}
        >
          <span className="hidden sm:inline">Next Week</span>
          <span className="sm:hidden">Next</span>
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Collapsible Day Sections */}
      <div className="space-y-3">
        {sortedDays.length === 0 ? (
          <div className="text-center py-8 text-clinic-muted">
            <p>No availability this week.</p>
            <p className="text-sm mt-1">Use the navigation above or jump to the next available week below.</p>
          </div>
        ) : (
          sortedDays.map(dateKey => {
            const daySlots = slotsByDay[dateKey];
            const bookableCount = getBookableCount(daySlots);
            const isFullyBooked = bookableCount === 0;
            const isExpanded = expandedDays.has(dateKey);

            return (
              <div key={dateKey} className="border rounded-lg overflow-hidden">
                {/* Day header - clickable */}
                <button
                  onClick={() => toggleDay(dateKey)}
                  disabled={isFullyBooked}
                  className={`flex items-center gap-3 w-full p-3 text-left transition-colors ${
                    isFullyBooked
                      ? 'bg-clinic-surface-sunken cursor-not-allowed'
                      : 'bg-clinic-surface hover:bg-clinic-surface-sunken'
                  }`}
                >
                  <span className={`text-clinic-muted transition-transform duration-200 ${
                    isExpanded ? 'rotate-90' : ''
                  } ${isFullyBooked ? 'invisible' : ''}`}>
                    ▶
                  </span>
                  <span className={`font-semibold ${isFullyBooked ? 'text-clinic-muted' : 'text-clinic-dark'}`}>
                    {formatDateDisplay(dateKey)}
                  </span>
                  <span className={`text-sm ${isFullyBooked ? 'text-clinic-muted' : 'text-clinic-muted'}`}>
                    {isFullyBooked
                      ? '(fully booked)'
                      : `(${bookableCount} ${bookableCount === 1 ? 'slot' : 'slots'} available)`
                    }
                  </span>
                </button>

                {/* Available start times for selected duration */}
                {isExpanded && !isFullyBooked && (
                  <div className="p-3 border-t border-clinic-primary-lighter bg-white">
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {getAvailableStartTimes(daySlots).map(slot => {
                        const isSelected = selectedStartSlot &&
                          selectedStartSlot.date === slot.date &&
                          selectedStartSlot.hour === slot.hour &&
                          selectedStartSlot.minute === slot.minute;

                        return (
                          <button
                            key={`${slot.date}-${slot.hour}-${slot.minute}`}
                            onClick={() => handleStartTimeClick(slot)}
                            className={`px-2 py-2.5 min-h-[44px] rounded text-sm font-medium transition-colors border ${
                              isSelected
                                ? 'bg-clinic-primary text-white border-clinic-primary'
                                : 'bg-clinic-primary/10 text-clinic-primary hover:bg-clinic-primary/20 border-clinic-primary/30'
                            }`}
                          >
                            {formatSlotTime(slot.hour, slot.minute)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Jump to Next Available CTA */}
      {findNextAvailableWeek !== null && (
        <div className="mt-4 text-center">
          <button
            onClick={jumpToNextAvailable}
            className="inline-flex items-center gap-2 px-4 py-2 bg-clinic-primary/10 text-clinic-primary rounded-lg hover:bg-clinic-primary/20 transition-colors text-sm font-medium border border-clinic-primary/20"
          >
            Jump to Next Available
            <ChevronDoubleRightIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* No more availability message */}
      {weekOffset < 3 && findNextAvailableWeek === null && sortedDays.length > 0 && getBookableCount(sortedDays.flatMap(d => slotsByDay[d])) === 0 && (
        <div className="mt-4 text-center text-clinic-muted text-sm">
          <p>No more availability in the next 4 weeks.</p>
        </div>
      )}
      </>
      )}

      {/* Notes and Booking Section */}
      <div className="mt-6 space-y-4 border-t border-clinic-primary-lighter pt-6">
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-clinic-dark mb-2">
            Notes (optional)
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes for your appointment..."
            className="w-full px-3 py-2 border border-clinic-primary-light rounded-lg focus:outline-none focus:ring-2 focus:ring-clinic-primary/30 focus:border-clinic-primary"
            rows={3}
            maxLength={500}
          />
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleBooking}
            disabled={!selectedStartSlot || isBooking}
            className="px-6 py-2 bg-clinic-primary text-white rounded-lg hover:bg-clinic-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isBooking
              ? (isApprovalMode ? 'Submitting...' : 'Booking...')
              : bookButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;
